import { Controller } from "@hotwired/stimulus"
import { createAlert, clearAlerts } from "../scripts/createAlert"

export default class extends Controller {
  static targets = [ "pdfTextTesterResult", "fileInput", "modalElement", "modalTextResultsContainer" ]
  static values = {
    helpUrl: String
  }

  already_acknowledged = false;
  static EXPECTED_TEXT_STRINGS = [
    // parsers
    "clippd", "hytek", "hy-tek", "meet manager", "meetpro", "gamechanger", "clippd",
    "golfstat", "virtius", "proscore", "opta",

    // golf
    "scoreboard", "round", "par", "invitational", "invite", "round 1", "round 2", "round 3",
    "rd 1", "rd 2", "rd 3",

    // common headers
    "heat", "total", "points", "place", "team", "player", "teams", "page 1",

    // trackwrestling
    "wrestlers", "wrestlers", "weight", "period 1", "period 2",

    // flag football
    "qtr", "time", "scoring play",

    // cross country
    "contractor license", "event",

    // soccer
    "stat perform"
  ]

  modal = null;

  async connect() {
    // console.log("Hello, PDF Text Tester Controller here!", this.element);

    this.modal = this.hasModalElementTarget && new bootstrap.Modal(this.modalElementTarget);

    // If already loaded, just use it
    if (window.pdfjsLib) {
      this.pdfLib = window.pdfjsLib;
      return;
    }

    // Otherwise, dynamically load it
    this.pdfjsLibLoadPromise = new Promise((resolve, reject) => {
      // Avoid duplicating script tags
      if (document.querySelector('script[src*="pdf.min.js"]')) {
        // Wait for the script to finish loading, in case it's still loading when this runs
        const waitForLib = () => {
          if (window.pdfjsLib) {
            this.pdfLib = window.pdfjsLib;
            resolve(window.pdfjsLib);
          } else {
            setTimeout(waitForLib, 100);
          }
        };
        waitForLib();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
      script.onload = () => {
        if (window.pdfjsLib) {
          this.pdfLib = window.pdfjsLib;
          resolve(window.pdfjsLib);
        } else {
          reject("PDF.js loaded but window.pdfjsLib is missing.");
        }
      };
      script.onerror = () => {
        reject("Failed to load PDF.js script.");
      };
      document.head.appendChild(script);
    });

    this.pdfjsLibLoadPromise.catch((e) => {
      console.error("PDF.js library failed to load:", e);
    });
  }

  handleFileSelect(event) {
    // console.log("handleFileSelect", event);
    // debugger;

    clearAlerts();

    const file = event.target.files[0]
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) return;

    if (this.already_acknowledged) {
      return;
    }

    // need to immediately stop the event from propagating, since reading the file is async
    event.preventDefault();
    event.stopImmediatePropagation();

    const reader = new FileReader();

    reader.onload = async (e) => {
      // debugger;
      try {
        const arrayBuffer = e.target.result;

        if (!this.pdfLib) {
          throw new Error("PDF.js library is not available on window.pdfjsLib.")
        }

        // Load PDF from the ArrayBuffer (run without a separate worker to simplify bundling)
        const loadingTask = this.pdfLib.getDocument({
          data: arrayBuffer,
          disableWorker: true,
        });
        const pdf = await loadingTask.promise;

        // For this tester, just inspect the first page
        const page = await pdf.getPage(1);
        const textContent = await page.getTextContent();

        const normalizedForMatch = this.normalizeTextForMatching(textContent);
        const matchedExpected = this.constructor.EXPECTED_TEXT_STRINGS.some((phrase) =>
          phrase && normalizedForMatch.includes(phrase.toLowerCase())
        );

        const rawExtracted = textContent.items.map((item) => item.str).join("");
        const nonWhitespaceCount = this.stripAllWhitespace(rawExtracted).length;

        const lineSeparated = textContent.items.map((item) => item.str).join("");

        // console.log("PDF text check", { matchedExpected, nonWhitespaceCount, textContent });
        // debugger;

        if (matchedExpected) {
          // success

          if (this.hasPdfTextTesterResultTarget) {
            this.pdfTextTesterResultTarget.innerHTML = "PDF text looks good: at least one expected phrase from our list was found on the first page."
          }

          this.acknowledgeTextTestResult();

          return true;

        } else if (nonWhitespaceCount === 0) {
          // error

          if (this.hasPdfTextTesterResultTarget) {
            this.pdfTextTesterResultTarget.innerHTML =
              "<strong>Error:</strong> No usable text on the first page. After removing all whitespace, nothing remains (image-only, empty, or whitespace-only extract).";
          }

          event.stopImmediatePropagation();
          // TODO warn the user that something has failed
          createAlert('danger', 'PDF has no text to extract! <a href="' + this.helpUrlValue + '">Learn more</a>');
          return false;

        } else {
          // no matches, but still has text
          if (this.hasPdfTextTesterResultTarget) {
            this.pdfTextTesterResultTarget.innerHTML =
              "<strong>No expected phrases matched.</strong> There is still non-whitespace content below—please confirm it looks reasonable before continuing, or stop if it does not.<br><br>" +
              "<strong>Extracted text from the first page:</strong><br><pre style=\"white-space:pre-wrap;word-break:break-word;max-height:20rem;overflow:auto;margin-top:0.5rem;padding:0.75rem;background:#f5f5f5;border-radius:4px;\">" +
              this.escapeHtml(lineSeparated) +
              "</pre>";
          }

          event.stopImmediatePropagation();

          if (this.hasModalTextResultsContainerTarget) {
            this.modalTextResultsContainerTarget.innerHTML = this.escapeHtml(lineSeparated);
          }

          this.modal.show();

          return false;
        }

      } catch (error) {
        console.error("Error while reading PDF with pdfjs-dist:", error);
        if (this.hasPdfTextTesterResultTarget) {
          const message =
            (error && (error.message || error.name)) ||
            "Unknown error while reading PDF.";

          this.pdfTextTesterResultTarget.textContent = `Error reading PDF: ${message}`;
        }
      }
    }

    reader.readAsArrayBuffer(file)
  }

  acknowledgeTextTestResult() {
    this.already_acknowledged = true;
    this.fileInputTarget.dispatchEvent(new Event('change'));
  }

  resetTextTestResult() {
    this.already_acknowledged = false;
  }

  /** Collapse whitespace and lowercase for substring checks against EXPECTED_TEXT_STRINGS. */
  normalizeTextForMatching(textContent) {
    return textContent.items
      .map((item) => item.str)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
  }

  /**
   * Remove all Unicode whitespace, line breaks (CR, LF), and invisible/zero-width characters,
   * including rarely-seen ones, to determine if anything "real" remains.
   * Intentionally exhaustive: covers Unicode whitespace, newlines, BOM, soft hyphen, etc.
   */
  stripAllWhitespace(str) {
    // Full Unicode whitespace list from ES2021 + common "invisible" chars
    // https://www.unicode.org/Public/UCD/latest/ucd/PropList.txt (White_Space)
    // This approach is robust across JS engines without needing the /u flag
    return String(str)
      // Remove all Unicode whitespace, separator chars, and explicit CR/LF
      .replace(/[\s\u00A0\u1680\u180E\u2000-\u200D\u2028\u2029\u202F\u205F\u2060\u3000\uFEFF\u00AD\r\n]/g, '')
      // Remove left-to-right/RTL marks and LRM/RLM chars if present
      .replace(/[\u200E\u200F\u202A-\u202E]/g, '')
      // Remove control characters in the C0 range (U+0000 through U+001F) and DEL (U+007F), plus some additional "non-printables"
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

}
